import React, { useContext, useEffect, useState } from "react";
import moment from "moment";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";
import TrendingDownIcon from "@mui/icons-material/TrendingDown";
import DangerousIcon from "@mui/icons-material/Dangerous";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Button,
  Checkbox,
  CircularProgress,
  FormControlLabel,
  Paper,
  Stack,
  Typography
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import {
  StyledContainer,
  StyledInnerContainer,
  StyledHeader,
  StyledHeaderText,
  StyledFallback
} from "./Notifications.styles";
import API from "api";
import Session from "helpers/session";
import {
  associateReadingType,
  toLastFirstName,
  toLocalTime
} from "helpers/variables";
import { useTranslation } from "react-i18next";
import { GlobalContext } from "context/Provider";
import Snackbar from "@mui/material/Snackbar";
import Alert from "@mui/material/Alert";
import { NotesObject } from "context/initialState/Notes";
import {loadMembersContext} from "../Main/Main.utils";
import { saveActiveEncounter } from "helpers/variables";

export default function Notifications() {
  const { t } = useTranslation();
  const { notifications, notificationsDispatch, member, memberDispatch, encounterTimerDispatch, settingsDispatch, encounterTimer } =
    useContext(GlobalContext);
  const [hasNotifications, setHasNotifications] = useState<boolean>(true);
  const [list, setList] = useState<any>();
  const [checked, setChecked] = React.useState<any>({});
  const [loading, setLoading] = useState(false);
  const [expandedAccordion, setExpandedAccordion] = React.useState<any>({});
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");

  const chunkArray = <T,>(arr: T[], chunkSize: number): T[][] => {
      const chunks: T[][] = [];
      for (let i = 0; i < arr.length; i += chunkSize) {
          chunks.push(arr.slice(i, i + chunkSize));
      }
      return chunks;
  };

  const handleSubmit = async () => {
    setLoading(true);
    try {
      const submissionArray = Object.entries(checked)
        .filter(([key, value]) => value && key.split("_")[0] !== "s")
        .map(([key]) => {
          const [userId, noticeId, readingId, indexString] = key.split("_");
          const index = parseInt(indexString, 10);
          const email = list[userId]?.[index]?.email;
          if (!email) throw new Error("No email provided");
          const memberId = member?.list.find(
            (m) => m.email === email
          )?.id;
          if (!memberId) throw new Error("memberId is missing, aborting submission");

          return {
            memberId,
            noticeId,
            readingId,
            authorId: Session.crackCookieUserId("coordinator"),
            authorName: Session.crackCookieUsername("coordinator"),
            content: "Acknowledged.",
            type: "action"
          };
        });

      const chunks = chunkArray(submissionArray, 30);

      for (const chunk of chunks) {
        await Promise.all(
          chunk.map(note =>
            API.kore.notes.create(note, Session.crackCookieToken("coordinator"))
          )
        );
      }

      generateAlertList();
      setChecked({});
      setExpandedAccordion({});
    } catch (err) {
      setSnackbarMessage(
        "Unable to acknowledge selected notifications. Please try again."
      );
      setSnackbarOpen(true);
    } finally {
      setLoading(false);
    }
  };

  const handleCheckSingle = (event: React.ChangeEvent<HTMLInputElement>) => {
    setChecked({
      ...checked,
      [`${event.target.id}_${event.target.name}`]:
        !checked[`${event.target.id}_${event.target.name}`]
    });
  };

  const handleCheckAll = (event: React.ChangeEvent<HTMLInputElement>) => {
    let alertGroup: any = {};
    let i = 0;
    while (i < parseInt(event.target.name)) {
      i++;
      alertGroup[
        `${event.target.id}_${list[event.target.id][i - 1]._id}_${
          list[event.target.id][i - 1].linkedReading
        }_${i - 1}`
      ] = !checked[`s_${event.target.id}`];
    }
    setChecked({
      ...checked,
      ...alertGroup,
      [`s_${event.target.id}`]: !checked[`s_${event.target.id}`]
    });
  };

  const handleExpand = (props: any) => {
    setExpandedAccordion({
      ...expandedAccordion,
      [props]: !expandedAccordion[props]
    });
  };

  const generateAlertList = async () => {
      try{
          const notificationCall = await API.kore.notifications.getList(
              Session.crackCookieToken("coordinator"),
              "acknowledged:false"
          );
          const notificationList = notificationCall.items;

          if(!notificationList.length) {
              setHasNotifications(false);
              setList({});
              notificationsDispatch({ type: "UPDATE_COUNT", payload: 0});
              return;
          }

          let noteList: any[] = [];
            try {
              noteList = await API.kore.notes.getList(
                Session.crackCookieToken("coordinator")
              );
            } catch {
              noteList = [];
            }

          const alertDictionary: Record<string, any[]> = {};

          notificationList.forEach((currentNotification: any) => {
              const linkedNote = noteList.find((currentNote: any) => {
                return currentNote.readingId === currentNotification.linkedReading;
              });

              const notificationWithNote = {
                  ...currentNotification,
                  note: linkedNote || null,
              };

              if(alertDictionary[currentNotification.memberId]) {
                  alertDictionary[currentNotification.memberId].push(notificationWithNote);
              } else {
                  alertDictionary[currentNotification.memberId] = [notificationWithNote];
              }
          });

          setList(alertDictionary);

          if(notificationList.length !== notifications?.itemsCount) {
              notificationsDispatch({
                  type: "UPDATE_COUNT",
                  payload: notificationList.length,
              });
          }
      } catch (err) {
          setHasNotifications(false);
          setList({});
          notificationsDispatch({ type: "UPDATE_COUNT", payload: 0});
      }
  };

  const generateAccordions = () => {
    const accordionArr = [];
    let id = 0;
    for (let pat in list) {
      id++;
      accordionArr.push(
        <AlertAccordion
          patient={list[pat]}
          patientId={list[pat][0].memberId}
          name={list[pat][0].username}
          selected={checked}
        />
      );
    }
    let n = 0;
    while (n > id) {
      n++;
      setExpandedAccordion({ ...expandedAccordion, [`${id}`]: false });
    }
    return accordionArr;
  };

  const AlertAccordion = (props?: any) => {
    const { patientId, patient, name } = props;
    const generateAlertVitals = (values: any, limits: any, level: any) => {
      let alertValueArr = [];
      for (let value in values) {
        let valueDifference: number;
        let trendIcon;
        let direction;
        if (limits[value]) {
          if (limits[value].high < values[value]) {
            trendIcon = (
              <TrendingUpIcon color={level === "high" ? "error" : "warning"} />
            );
            valueDifference = values[value] - limits[value].high;
            direction = "Higher";
          } else if (limits[value].low > values[value]) {
            trendIcon = (
              <TrendingDownIcon
                color={level === "high" ? "error" : "warning"}
              />
            );
            valueDifference = limits[value].high - values[value];
            direction = "Lower";
          } else {
            trendIcon = (
              <CheckCircleIcon color={"success"} sx={{ pr: 1, pt: 0.5 }} />
            );
            valueDifference = 0;
          }
          alertValueArr.push(
            <Stack
              direction="row"
              spacing={2}
              sx={{ border: `1px solid grey`, p: 0.5 }}
            >
              <b>{value}</b>
              <Stack spacing={2}>
                {`${Math.round(values[value])} ${limits[value].units}`}
                {trendIcon}
                {valueDifference !== 0
                  ? `${direction} by ${Math.round(valueDifference)} ${limits[value].units}`
                  : ""}
              </Stack>
            </Stack>
          );
        }
      }
      return (
        <Stack direction={"row"} spacing={6}>
          {alertValueArr}
        </Stack>
      );
    };

    const AlertItem = (props: any) => {
      const { indexLocation, alerts, level, id, breach, reading } = props;
      return (
        <Paper sx={{ pt: 1, pb: 1 }}>
          <Stack direction="row" alignItems={"center"} sx={{ p: 0.5, pl: 3 }}>
            <FormControlLabel
              label=""
              control={
                <Checkbox
                  name={`${breach}_${reading}_${indexLocation}`}
                  id={id}
                  checked={
                    checked[`${id}_${breach}_${reading}_${indexLocation}`]
                  }
                  onChange={handleCheckSingle}
                />
              }
            />
            <Typography sx={{ width: 160 }}>
              {associateReadingType(alerts.readingType)}
            </Typography>
            <Stack
              direction={"row"}
              spacing={6}
              justifyContent={"space-evenly"}
              alignItems={"center"}
            >
              <Stack direction={"row"} alignItems={"center"} spacing={2}>
                <Typography>Warning Level:</Typography>
                {level === "high" ? (
                  <Stack direction={"row"} alignItems={"center"} spacing={.5}>
                    <DangerousIcon color="error" fontSize="large" />
                    <Typography> Severe </Typography>
                  </Stack>
                ) : (
                  <Stack direction={"row"} alignItems={"center"} spacing={.5}>
                    <WarningAmberIcon color="warning" fontSize="large" />
                    <Typography> Moderate </Typography>
                  </Stack>
                )}
              </Stack>
              <p>
                {moment(toLocalTime(alerts.receivedAt)).format(
                  "MM/DD/YYYY hh:mm A"
                )}
              </p>

              {generateAlertVitals(
                alerts.readingValue,
                alerts.threshold,
                level
              )}

              {alerts?.note && (
                  <Typography
                    sx={{ paddingRight: 1 }}
                  >
                      {alerts?.note.content}
                  </Typography>
              )}
            </Stack>
          </Stack>
        </Paper>
      );
    };

    return (
      <Accordion
        expanded={expandedAccordion[patientId]}
        sx={{ border: 1, backgroundColor: "#E7E7E7" }}
      >
        <AccordionSummary
          onClick={() => {
            handleExpand(patientId);
          }}
          expandIcon={<ExpandMoreIcon />}
          aria-controls={`panel-contents-${patientId}`}
          id={`panel-header-${patientId}`}
        >
          <Stack direction={"row"} alignItems={"center"}>
            <b>{toLastFirstName(name)}</b>
          </Stack>
        </AccordionSummary>
        <AccordionDetails>
          <FormControlLabel
            label="Select All"
            control={
              <Checkbox
                id={patientId}
                name={patient.length}
                onChange={handleCheckAll}
                checked={checked[`s_${patientId}`]}
              />
            }
            sx={{ pb: 2 }}
          />
          <Stack spacing={0.8}>
            {patient.map((M: any, index: any) => {
              if (M.breachType === "high") {
                return (
                  <AlertItem
                    alerts={M}
                    indexLocation={index}
                    level={"high"}
                    id={patientId}
                    breach={M._id}
                    reading={M.linkedReading}
                  />
                );
              }
            })}
            {patient.map((M: any, index: any) => {
              if (M.breachType === "moderate") {
                return (
                  <AlertItem
                    alerts={M}
                    indexLocation={index}
                    level={"high"}
                    id={patientId}
                    breach={M._id}
                    reading={M.linkedReading}
                  />
                );
              }
            })}
          </Stack>
        </AccordionDetails>
      </Accordion>
    );
  };

  useEffect(() => {
    saveActiveEncounter(member, encounterTimerDispatch, settingsDispatch, encounterTimer?.current?.duration || 0);
    loadMembersContext(memberDispatch);
    generateAlertList();
  }, []);

  return (
    <StyledContainer>
      <StyledHeader>
        <StyledHeaderText>{t("u-notifications")}</StyledHeaderText>
      </StyledHeader>

      {hasNotifications ? (
        <>
          <Button
            sx={{ 
              marginTop: 2, 
              marginLeft: 3,
              minWidth: 150, 
              minHeight: 45,
              position: "relative", 
            }}
            variant="contained"
            disabled={loading || Object.keys(checked).length > 0 ? false : true}
            onClick={handleSubmit}
          >
            {loading ? <CircularProgress size={25} sx={{ color: "white" }}/> : "Acknowledge"}
          </Button>
          <StyledInnerContainer>{generateAccordions()}</StyledInnerContainer>
        </>
      ) : (
        <Stack
          direction={"column"}
          justifyContent={"center"}
          alignItems={"center"}
        >
          <h1>{"No notifications"}</h1>
          <StyledFallback></StyledFallback>
        </Stack>
      )}
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={4000}
        onClose={() => setSnackbarOpen(false)}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Alert
          onClose={() => setSnackbarOpen(false)}
          severity="error"
          sx={{ width: "100%" }}
        >
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </StyledContainer>
  );
}
